import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  startOfWeekMonday,
  addDays,
  buildSchedulerModel,
  getHourColumns,
  blocksForResourceDay,
  SCHEDULER_HOUR_START,
  SCHEDULER_HOUR_END,
  blockTimePosition,
} from '../utils/scheduleToScheduler';
import './LaborResourceScheduler.css';

const SCHEDULER_MODES = {
  WEEK: 'week',
  DAY: 'day',
};

function isToday(dateKey) {
  return dateKey === new Date().toISOString().slice(0, 10);
}

function SchedulerBlock({ block, style, onHolidayClick }) {
  const handleClick = () => {
    if (block.holidayPayload && onHolidayClick) {
      onHolidayClick(block.holidayPayload);
    }
  };

  const className = [
    'labor-scheduler__block',
    block.allDay ? 'labor-scheduler__block--allday' : '',
    block.isHoliday ? 'labor-scheduler__block--holiday' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={className}
      style={style}
      title={`${block.timeLabel} — ${block.title}`}
      onClick={block.isHoliday ? handleClick : undefined}
      onKeyDown={
        block.isHoliday
          ? (e) => {
              if (e.key === 'Enter') handleClick();
            }
          : undefined
      }
      role={block.isHoliday ? 'button' : undefined}
      tabIndex={block.isHoliday ? 0 : undefined}
    >
      <span className="labor-scheduler__block-time">{block.timeLabel}</span>
      <span className="labor-scheduler__block-title">{block.title}</span>
    </div>
  );
}

function WeekCell({ blocks, isTodayCell, onHolidayClick }) {
  const laneHeight = 24;
  const cellMinHeight = Math.max(52, blocks.length * laneHeight + 8);

  return (
    <div
      className={`labor-scheduler__cell ${isTodayCell ? 'labor-scheduler__cell--today' : ''}`}
      style={{ minHeight: cellMinHeight }}
    >
      {blocks.map((block) => {
        const top = 4 + (block.lane ?? 0) * laneHeight;
        if (!block.allDay && !block.position) return null;
        const style = block.allDay
          ? { top, left: 2, right: 2, backgroundColor: block.color }
          : {
              top,
              left: `${block.position.left}%`,
              width: `${block.position.width}%`,
              backgroundColor: block.color,
            };
        return (
          <SchedulerBlock
            key={block.id}
            block={block}
            style={style}
            onHolidayClick={onHolidayClick}
          />
        );
      })}
    </div>
  );
}

export const LaborResourceScheduler = ({
  events = [],
  anchorDate,
  loading = false,
  onHolidayClick,
}) => {
  const [weekMonday, setWeekMonday] = useState(() => startOfWeekMonday(anchorDate || new Date()));
  const [mode, setMode] = useState(SCHEDULER_MODES.WEEK);
  const [dayIndex, setDayIndex] = useState(() => {
    const anchor = anchorDate ? new Date(`${String(anchorDate).slice(0, 10)}T12:00:00`) : new Date();
    const monday = startOfWeekMonday(anchor);
    const diff = Math.round((anchor - monday) / (86400000));
    return Math.max(0, Math.min(6, diff));
  });

  useEffect(() => {
    setWeekMonday(startOfWeekMonday(anchorDate || new Date()));
  }, [anchorDate]);

  const { resources, blocks, weekDays } = useMemo(
    () => buildSchedulerModel(events, weekMonday),
    [events, weekMonday]
  );

  const hourColumns = useMemo(() => getHourColumns(), []);

  const weekLabel = useMemo(() => {
    const end = addDays(weekMonday, 6);
    const opts = { day: 'numeric', month: 'short', year: 'numeric' };
    return `${weekMonday.toLocaleDateString('es-ES', opts)} – ${end.toLocaleDateString('es-ES', opts)}`;
  }, [weekMonday]);

  const focusedDay = weekDays[dayIndex] ?? weekDays[0];

  const goWeek = useCallback((delta) => {
    setWeekMonday((prev) => addDays(prev, delta * 7));
  }, []);

  const goToday = useCallback(() => {
    const now = new Date();
    const monday = startOfWeekMonday(now);
    setWeekMonday(monday);
    const diff = Math.round((now - monday) / 86400000);
    setDayIndex(Math.max(0, Math.min(6, diff)));
  }, []);

  if (!loading && resources.length === 0) {
    return (
      <div className="labor-scheduler relative min-h-[200px]">
        <p className="labor-scheduler__empty">
          Sin turnos en esta semana. Cambia de semana o revisa filtros (patrón / empleado / año).
        </p>
      </div>
    );
  }

  return (
    <div className="labor-scheduler relative min-h-[320px]">
      {loading && (
        <div className="labor-scheduler__loading">
          <span>Cargando scheduler…</span>
        </div>
      )}

      <div className="labor-scheduler__toolbar">
        <button
          type="button"
          className="labor-scheduler__nav-btn"
          onClick={() => goWeek(-1)}
          aria-label="Semana anterior"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="labor-scheduler__toolbar-title">{weekLabel}</span>
        <button
          type="button"
          className="labor-scheduler__nav-btn"
          onClick={() => goWeek(1)}
          aria-label="Semana siguiente"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        <button type="button" className="labor-scheduler__mode-btn ml-2" onClick={goToday}>
          Hoy
        </button>
        <div className="flex gap-1 ml-auto">
          <button
            type="button"
            className={`labor-scheduler__mode-btn ${mode === SCHEDULER_MODES.WEEK ? 'is-active' : ''}`}
            onClick={() => setMode(SCHEDULER_MODES.WEEK)}
          >
            Semana
          </button>
          <button
            type="button"
            className={`labor-scheduler__mode-btn ${mode === SCHEDULER_MODES.DAY ? 'is-active' : ''}`}
            onClick={() => setMode(SCHEDULER_MODES.DAY)}
          >
            Día (horas)
          </button>
        </div>
      </div>

      {mode === SCHEDULER_MODES.DAY && (
        <div className="flex flex-wrap gap-1 px-3 py-2 border-b border-gray-700 bg-gray-800/50">
          {weekDays.map((d, i) => (
            <button
              key={d.dateKey}
              type="button"
              onClick={() => setDayIndex(i)}
              className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                i === dayIndex
                  ? 'bg-blue-600 border-blue-500 text-white'
                  : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>
      )}

      <div className="labor-scheduler__scroll">
        {mode === SCHEDULER_MODES.WEEK && (
          <div className="labor-scheduler__grid labor-scheduler__grid--week">
            <div className="labor-scheduler__head-cell labor-scheduler__head-cell--corner">
              Recurso / turno
            </div>
            {weekDays.map((d) => (
              <div
                key={d.dateKey}
                className={`labor-scheduler__head-cell ${isToday(d.dateKey) ? 'labor-scheduler__head-cell--today' : ''}`}
              >
                {d.label}
              </div>
            ))}
            {resources.map((resource) => (
              <React.Fragment key={resource.id}>
                <div className="labor-scheduler__row-label">{resource.label}</div>
                {weekDays.map((d) => (
                  <WeekCell
                    key={`${resource.id}-${d.dateKey}`}
                    blocks={blocksForResourceDay(blocks, resource.id, d.dateKey)}
                    isTodayCell={isToday(d.dateKey)}
                    onHolidayClick={onHolidayClick}
                  />
                ))}
              </React.Fragment>
            ))}
          </div>
        )}

        {mode === SCHEDULER_MODES.DAY && focusedDay && (
          <div
            className="labor-scheduler__grid labor-scheduler__grid--day"
            style={{
              gridTemplateColumns: `var(--labor-sch-row-label-w) repeat(${hourColumns.length}, minmax(40px, 1fr))`,
            }}
          >
            <div className="labor-scheduler__head-cell labor-scheduler__head-cell--corner">
              {focusedDay.labelFull}
            </div>
            {hourColumns.map((col) => (
              <div key={col.hour} className="labor-scheduler__head-cell">
                {col.label}
              </div>
            ))}
            {resources.map((resource) => (
              <React.Fragment key={resource.id}>
                <div className="labor-scheduler__row-label">{resource.label}</div>
                <div
                  className="labor-scheduler__cell labor-scheduler__cell--hour labor-scheduler__cell--today"
                  style={{
                    gridColumn: `2 / span ${hourColumns.length}`,
                  }}
                >
                  {blocksForResourceDay(blocks, resource.id, focusedDay.dateKey).map((block) => {
                    const laneHeight = 36;
                    const top = 6 + (block.lane ?? 0) * laneHeight;
                    if (block.allDay) {
                      return (
                        <SchedulerBlock
                          key={block.id}
                          block={block}
                          style={{
                            top,
                            left: 4,
                            right: 4,
                            backgroundColor: block.color,
                          }}
                          onHolidayClick={onHolidayClick}
                        />
                      );
                    }
                    const pos =
                      block.position ||
                      blockTimePosition(block.start, block.end, block.dateKey);
                    return (
                      <SchedulerBlock
                        key={block.id}
                        block={block}
                        style={{
                          top,
                          left: `${pos.left}%`,
                          width: `${pos.width}%`,
                          backgroundColor: block.color,
                        }}
                        onHolidayClick={onHolidayClick}
                      />
                    );
                  })}
                </div>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      <div className="labor-scheduler__legend">
        <span>
          <strong className="text-gray-300">Semana:</strong> tiempo horizontal dentro de cada día
          ({SCHEDULER_HOUR_START}:00–{SCHEDULER_HOUR_END}:00).
        </span>
        <span>
          <strong className="text-gray-300">Día:</strong> eje horizontal por horas; una fila por
          patrón o empleado.
        </span>
        <span>
          Varios turnos en la misma fila si no se solapan; carril extra solo cuando coinciden en
          horario (p. ej. dos patrones a la vez).
        </span>
      </div>
    </div>
  );
};
